import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Loader2 } from 'lucide-react';
import { useTheme } from '../ThemeProvider';
import { businessTokens } from './businessTheme';
import { HScrollDeck } from '../ui/HScrollDeck';
import { BusinessService } from '../../services/BusinessService';
import type { BusinessProfile } from '../../types/business';

/**
 * The Explore business deck.
 *
 * Shuffled on every load rather than carrying a stored order, per spec: a fixed
 * order would quietly become a ranking, and the first businesses to register
 * would own the front of the deck forever. Fisher-Yates lives in
 * BusinessService.getExploreDeck.
 *
 * Uses HScrollDeck — the same primitive the Similar Users and Newest Yappers
 * rows are built on — so this row scrolls, snaps and shows its arrows exactly
 * like the rows above and below it.
 *
 * Login-gated by the caller: this renders inside ExploreScreen, which already
 * sits behind ProtectedRoute.
 */
export function BusinessExploreDeck({ refreshKey = 0 }: { refreshKey?: number }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const t = businessTokens(theme);

  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const deck = await BusinessService.getExploreDeck(20);
      if (!active) return;
      setProfiles(deck);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [refreshKey]);

  // A deck with nothing in it is not a section. Before any business exists this
  // renders nothing rather than an empty shelf.
  if (!loading && profiles.length === 0) return null;

  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Briefcase style={{ width: 24, height: 24, color: t.dark ? '#4478ea' : '#F39C12' }} />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.dark ? '#ffffff' : '#ea580c' }}>
          Businesses
        </h2>
      </div>

      <HScrollDeck paddingTop={4} paddingBottom={6}>
        {loading
          ? [1, 2, 3].map(i => (
              <div
                key={i}
                style={{ flexShrink: 0, width: 'calc((100% - 24px) / 2)', maxWidth: 220 }}
              >
                <div
                  style={{
                    width: '100%', aspectRatio: '4 / 3', borderRadius: 16,
                    background: t.inset, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Loader2 style={{ width: 18, height: 18, color: t.textMuted }} className="animate-spin" />
                </div>
              </div>
            ))
          : profiles.map(profile => (
              <BusinessDeckCard
                key={profile.id}
                profile={profile}
                onTap={() => navigate(`/business/${profile.id}`)}
              />
            ))}
      </HScrollDeck>
    </section>
  );
}

/**
 * One business in the deck. Leads with the first portfolio image, because that
 * is the thing a business is actually selling — the name is the caption.
 */
export function BusinessDeckCard({ profile, onTap }: { profile: BusinessProfile; onTap: () => void }) {
  const { theme } = useTheme();
  const t = businessTokens(theme);
  const cover = profile.portfolioImages?.[0];

  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={onTap}
      style={{
        flexShrink: 0, width: 'calc((100% - 24px) / 2)', maxWidth: 220,
        padding: 0, border: 'none', background: 'none', cursor: 'pointer',
        textAlign: 'left', alignSelf: 'flex-start',
      }}
    >
      <div
        style={{
          width: '100%', aspectRatio: '4 / 3', borderRadius: 16, overflow: 'hidden',
          background: t.inset, border: `1px solid ${t.border}`,
        }}
      >
        {cover
          ? <img src={cover} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Briefcase style={{ width: 22, height: 22, color: t.textMuted }} />
            </div>
          )}
      </div>
      <p
        style={{
          margin: '8px 2px 0', fontSize: 13, fontWeight: 700, color: t.dark ? '#ffffff' : '#1a1a1a',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {profile.businessName}
      </p>
      {profile.description && (
        <p
          style={{
            margin: '2px 2px 0', fontSize: 11, lineHeight: 1.35, color: t.textMuted,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {profile.description}
        </p>
      )}
    </motion.button>
  );
}
